import styles from './header.module.less';
import { Button, Flex, Popover } from 'antd';
import { IOrganizationMember } from '@copilot/common/store/models/redux';
import isNil from 'lodash/isNil';
import { LinkedInStatusTag } from '../../linkedInStatusTag/linkedInStatusTag';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseCircleFilled, ReloadOutlined, CheckCircleFilled } from '@ant-design/icons';
import { HAS_DISMISSED_ACCOUNT_ERROR_STATUS_STORAGE_KEY } from './constant';
import debounce from 'lodash/debounce';

type LinkedInStatusIndicatorProps = { currentMember: IOrganizationMember | null };

/**
 * Indicator for the status of the LinkedIn connection (either connected, syncing, or disconnected)
 * @param param0
 * @returns
 */
export function LinkedInStatusIndicator({ currentMember }: LinkedInStatusIndicatorProps) {
    const [isPopoverOpen, setIsPopoverOpen] = useState<boolean | undefined>(undefined);
    const [hasCalculatedInitialOpenState, setHasCalculatedInitialOpenState] = useState(false);
    const isMemberLoading =
        isNil(currentMember) ||
        isNil(currentMember.isLiSessionInRetryState) ||
        isNil(currentMember.isLinkedInLoggedIn);
    const hasDismissedStatusWarning = localStorage.getItem(
        HAS_DISMISSED_ACCOUNT_ERROR_STATUS_STORAGE_KEY
    );
    const navigate = useNavigate();

    const debouncedSetIsPopoverOpen = useCallback(
        debounce((value: boolean | undefined) => {
            setIsPopoverOpen(value);
        }, 250),
        []
    );

    useEffect(() => {
        if (
            isMemberLoading ||
            (currentMember.isLinkedInLoggedIn === false && currentMember.isLiSessionInRetryState) ||
            (currentMember.isLinkedInLoggedIn && !currentMember.hasInvalidSalesNavSubscription) ||
            hasDismissedStatusWarning
        ) {
            debouncedSetIsPopoverOpen(undefined);
        } else if (!hasCalculatedInitialOpenState) {
            debouncedSetIsPopoverOpen(true);
            setHasCalculatedInitialOpenState(true);
        }
    }, [isMemberLoading, currentMember]);

    function getPopOverTitle(member: IOrganizationMember | null) {
        if (isNil(member)) {
            return undefined;
        }
        const { isLiSessionInRetryState, isLinkedInLoggedIn, hasInvalidSalesNavSubscription } =
            member;
        if (isLinkedInLoggedIn && !hasInvalidSalesNavSubscription) {
            return (
                <Flex align="center" gap={8}>
                    <CheckCircleFilled className={styles.successIcon} />
                    Connected
                </Flex>
            );
        } else if (isLinkedInLoggedIn === false && isLiSessionInRetryState) {
            return (
                <Flex align="center" gap={8}>
                    <ReloadOutlined className={styles.warningIcon} />
                    Syncing in-progress
                </Flex>
            );
        } else if (isLinkedInLoggedIn && hasInvalidSalesNavSubscription) {
            return (
                <Flex align="center" gap={8}>
                    <CloseCircleFilled className={styles.errorIcon} />
                    Sales Navigator missing
                </Flex>
            );
        } else {
            return (
                <Flex align="center" gap={8}>
                    <CloseCircleFilled className={styles.errorIcon} />
                    Disconnected
                </Flex>
            );
        }
    }

    function getPopOverContent(member: IOrganizationMember | null) {
        if (isNil(member)) {
            return undefined;
        }
        const { isLiSessionInRetryState, isLinkedInLoggedIn, hasInvalidSalesNavSubscription } =
            member;
        if (isLinkedInLoggedIn && !hasInvalidSalesNavSubscription) {
            return 'Your LinkedIn account is connected.';
        } else if (isLinkedInLoggedIn === false && isLiSessionInRetryState) {
            return 'Your LinkedIn account is syncing. During this time, messages and search list uploads are temporarily unavailable. This may take a while, please check back later.';
        } else if (isLinkedInLoggedIn && hasInvalidSalesNavSubscription) {
            return (
                <div>
                    Please confirm your subscription to Sales Navigator is active and reconnect your
                    LinkedIn account to resume running automation.
                    <Flex justify="flex-end" gap={8} className={styles.statusPopoverButtonSection}>
                        {!hasDismissedStatusWarning && <Button onClick={onCancel}>Dismiss</Button>}
                        <Button type="primary" onClick={onReconnect}>
                            Reconnect
                        </Button>
                    </Flex>
                </div>
            );
        } else {
            return (
                <div>
                    Please reconnect your LinkedIn account to resume automation.
                    <Flex justify="flex-end" gap={8} className={styles.statusPopoverButtonSection}>
                        {!hasDismissedStatusWarning && <Button onClick={onCancel}>Dismiss</Button>}
                        <Button type="primary" onClick={onReconnect}>
                            Reconnect
                        </Button>
                    </Flex>
                </div>
            );
        }
    }

    function getStatusTag(member: IOrganizationMember | null) {
        if (isNil(member)) {
            return <LinkedInStatusTag status="loading" label="Checking status..." />;
        }
        const { isLiSessionInRetryState, isLinkedInLoggedIn, hasInvalidSalesNavSubscription } =
            member;
        if (isLinkedInLoggedIn && !hasInvalidSalesNavSubscription) {
            return <LinkedInStatusTag status="success" label="Connected" />;
        } else if (isLinkedInLoggedIn === false && isLiSessionInRetryState) {
            return <LinkedInStatusTag status="warning" label="Syncing" />;
        } else if (isLinkedInLoggedIn && hasInvalidSalesNavSubscription) {
            return <LinkedInStatusTag status="error" label="Sales Nav Missing" />;
        } else {
            return <LinkedInStatusTag status="error" label="Disconnected" />;
        }
    }

    function onReconnect() {
        setIsPopoverOpen(undefined);
        navigate('/settings');
    }

    function onCancel() {
        // Save the "hasDismissedStatusWarning" flag to sessionStorage so we don't show the warning again within the same session
        localStorage.setItem(HAS_DISMISSED_ACCOUNT_ERROR_STATUS_STORAGE_KEY, 'true');
        setIsPopoverOpen(undefined);
    }

    return (
        <div className={styles.linkedInStatusIndicator}>
            <Popover
                placement="bottomLeft"
                title={getPopOverTitle(isMemberLoading ? null : currentMember)}
                trigger="hover"
                open={isPopoverOpen}
                overlayClassName={styles.linkedInStatusPopOverOverlay}
                content={
                    isMemberLoading ? undefined : (
                        <div className={styles.linkedInStatusPopOverContent}>
                            {getPopOverContent(currentMember)}
                        </div>
                    )
                }
            >
                {getStatusTag(isMemberLoading ? null : currentMember)}
            </Popover>
        </div>
    );
}
